import { useState } from "react";
import { Link } from "../../lib/i18n";
import { toast } from "sonner";
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin, Clock, Landmark, Banknote, CreditCard, ShieldCheck } from "lucide-react";
import { Logo } from "./Header";
import { useSite } from "../../context/SiteContext";
import { api, errMsg } from "../../lib/api";

const COLS = [
  ["Boutique", [["Bois de chauffage", "/boutique?univers=bois"], ["Poêles & inserts", "/poeles"], ["Granulés", "/granules"], ["Accessoires", "/accessoires"], ["Packs & offres", "/packs"]]],
  ["Services", [["Calculateur de bois", "/calculateur"], ["Livraison", "/livraison"], ["Installation", "/installation"], ["Demander un devis", "/devis"], ["Besoin d'aide ?", "/aide"], ["FAQ", "/livraison#faq"]]],
  ["Maison", [["À propos", "/a-propos"], ["Le guide de la chaleur", "/conseils"], ["Contact", "/contact"], ["Mon compte", "/compte"]]],
];
const LEGAL = [["Mentions légales", "mentions-legales"], ["CGV", "cgv"], ["Confidentialité", "confidentialite"], ["Cookies", "cookies"], ["Livraison", "politique-livraison"], ["Retours", "retours"], ["Conditions d'utilisation", "conditions-utilisation"], ["Données personnelles", "donnees-personnelles"]];

export function NewsletterForm({ dark = true, source = "footer" }) {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email, source }); toast.success("Merci ! Vous êtes inscrit(e) à notre lettre."); setEmail(""); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid={`newsletter-form-${source}`}>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Votre adresse email"
        className={`w-full rounded-md px-4 py-3 text-sm outline-none ${dark ? "border border-ivoire/20 bg-ivoire/5 text-ivoire placeholder:text-ivoire/40 focus:border-laiton" : "field"}`} data-testid={`newsletter-email-${source}`} />
      <button className="btn-accent !px-5 shrink-0" data-testid={`newsletter-submit-${source}`}>S'inscrire</button>
    </form>
  );
}

const PayPalMark = () => <span className="font-sans text-[15px] font-extrabold italic tracking-tight"><span className="text-[#003087]">Pay</span><span className="text-[#009CDE]">Pal</span></span>;
const PAY_ICON = { bank_transfer: Landmark, cash_on_delivery: Banknote, card: CreditCard };

function PayLogo({ m }) {
  const I = PAY_ICON[m.id] || ShieldCheck;
  return (
    <span className="flex h-9 items-center gap-2 rounded-md bg-ivoire px-3 text-[12px] font-medium text-brun shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`footer-pay-${m.id}`} title={m.name}>
      {m.id === "paypal" ? <PayPalMark /> : <><I size={15} className="text-bois" />{m.name}</>}
    </span>
  );
}

export default function Footer() {
  const { site, payment_methods = [] } = useSite();
  const c = site?.contact || {};
  return (
    <footer className="bg-ebene text-ivoire/70 grain relative pb-20 xl:pb-0">
      <div className="container-x relative grid gap-12 py-20 lg:grid-cols-12">
        <div className="lg:col-span-4 space-y-6">
          <Logo light />
          <p className="max-w-sm text-sm leading-relaxed">{site?.tagline}. Bois sélectionné, poêles choisis avec exigence et livraison organisée avec soin.</p>
          <div className="space-y-2.5 text-sm">
            <p className="flex gap-3"><Phone size={15} className="text-laiton mt-0.5" />{c.phone}</p>
            <p className="flex gap-3"><Mail size={15} className="text-laiton mt-0.5" />{c.email}</p>
            <p className="flex gap-3"><MapPin size={15} className="text-laiton mt-0.5" />{c.address}</p>
            <p className="flex gap-3"><Clock size={15} className="text-laiton mt-0.5" />{c.hours}</p>
          </div>
        </div>
        {COLS.map(([t, links]) => (
          <div key={t} className="lg:col-span-2">
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.24em] text-laiton">{t}</p>
            <ul className="space-y-3 text-sm">{links.map(([l, to]) => <li key={l}><Link to={to} className="link-u hover:text-ivoire">{l}</Link></li>)}</ul>
          </div>
        ))}
        <div className="lg:col-span-2 space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-laiton">Lettre de saison</p>
          <p className="text-sm">Conseils, rappels d'avant-hiver et offres réservées.</p>
          <NewsletterForm />
          <div className="flex gap-3 pt-2">
            {[[Facebook, site?.social?.facebook], [Instagram, site?.social?.instagram], [Youtube, site?.social?.youtube]].map(([I, href], i) => (
              <a key={i} href={href || "#"} className="grid h-9 w-9 place-items-center rounded-full border border-ivoire/15 hover:border-laiton hover:text-laiton transition-colors" aria-label="Media społecznościowe"><I size={15} /></a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-ivoire/10">
        <div className="container-x flex flex-col gap-4 py-6 text-xs lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" data-testid="footer-payment-logos">
            <span className="mr-1 flex items-center gap-1.5 text-ivoire/60"><ShieldCheck size={14} className="text-laiton" />Paiement sécurisé</span>
            {payment_methods.filter((m) => m.enabled).map((m) => <PayLogo key={m.id} m={m} />)}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">{LEGAL.map(([l, s]) => <Link key={s} to={`/legal/${s}`} className="hover:text-ivoire">{l}</Link>)}</div>
          <p>© {new Date().getFullYear()} {site?.brand_name}{c.company ? ` · ${c.company}` : ""}{c.krs ? ` · ${c.krs}` : ""}</p>
        </div>
      </div>
    </footer>
  );
}
